import React, { useState } from 'react';

const faqData = [
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'What is BragSheet?',
        a: 'An AI-powered journaling app that helps high school students track activities, achievements, and reflections for college applications.',
      },
      {
        q: 'Is BragSheet free?',
        a: 'Yes, BragSheet is currently free during our beta period.',
      },
      {
        q: 'How do I create an account?',
        a: 'Click "Get Started" on the homepage and sign up with your email or Google account.',
      },
    ],
  },
  {
    category: 'Journal & Entries',
    questions: [
      {
        q: 'What should I write about?',
        a: 'Anything meaningful — club meetings, volunteer work, awards, personal growth moments, challenges you overcame. The more you capture now, the easier applications will be later.',
      },
      {
        q: 'Can I edit or delete entries?',
        a: 'Yes. Click any entry to view and edit it, or use the delete button on the journal page.',
      },
      {
        q: 'What are categories and tags?',
        a: 'Categories (Academic, Athletic, Arts, etc.) organize entries by type. Tags are custom labels for specific themes like "leadership" or "public-speaking."',
      },
    ],
  },
  {
    category: 'AI Features',
    questions: [
      {
        q: 'How does AI auto-tagging work?',
        a: 'Our AI reads your entry and suggests relevant tags based on the content. You can accept, modify, or remove any suggestions.',
      },
      {
        q: 'What are essay brainstorm suggestions?',
        a: 'The AI analyzes your entries and activities to suggest compelling personal statement themes, with rationale for why each would work.',
      },
      {
        q: 'Is my data used to train AI models?',
        a: 'No. Your data is processed only to generate suggestions for you. It is not stored by the AI provider or used for training.',
      },
    ],
  },
  {
    category: 'Profile & Data',
    questions: [
      {
        q: 'Can I export my data?',
        a: 'Yes. Export your journal as JSON or CSV from the Journal page, and your profile as JSON from the Profile page.',
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to Profile, scroll to "Danger Zone," and click "Delete Account." This permanently removes all your data.',
      },
    ],
  },
  {
    category: 'Account & Security',
    questions: [
      {
        q: 'How do I enable two-factor authentication?',
        a: 'Go to Profile and scroll to the MFA section. You\'ll need an authenticator app like Google Authenticator or Authy.',
      },
      {
        q: 'I forgot my password.',
        a: 'Click "Forgot password?" on the login page to receive a reset code via email.',
      },
      {
        q: 'How is my data stored?',
        a: 'Encrypted at rest in AWS, transmitted over HTTPS. See our Privacy Policy for full details.',
      },
    ],
  },
];

function FaqItem({ question, answer, isOpen, onToggle }) {
  return (
    <div style={{
      borderBottom: '1px solid #e2e8f0',
    }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          padding: '1rem 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
          fontSize: '0.9rem',
          fontWeight: '500',
          color: '#1e293b',
        }}
      >
        <span>{question}</span>
        <span style={{
          fontSize: '0.85rem',
          color: '#64748b',
          marginLeft: '1rem',
          flexShrink: 0,
        }}>
          {isOpen ? '\u25BE' : '\u25B8'}
        </span>
      </button>
      {isOpen && (
        <p style={{
          color: '#475569',
          fontSize: '0.9rem',
          lineHeight: '1.7',
          margin: '0 0 1rem 0',
          paddingRight: '1.5rem',
        }}>
          {answer}
        </p>
      )}
    </div>
  );
}

export default function FaqPage() {
  const [expanded, setExpanded] = useState(new Set());

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '2rem',
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        padding: '2.5rem',
        width: '100%',
        maxWidth: '760px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        marginTop: '1rem',
        marginBottom: '2rem',
      }}>
        <a href="/" style={{ color: '#01696F', fontSize: '0.85rem', textDecoration: 'none' }}>&larr; Back to home</a>

        <h1 style={{ color: '#1e293b', fontSize: '1.5rem', fontWeight: '700', marginTop: '1.5rem', marginBottom: '2rem' }}>Frequently Asked Questions</h1>

        {faqData.map((section, sIdx) => (
          <div key={section.category}>
            <h2 style={{ color: '#1e293b', fontSize: '1.15rem', fontWeight: '600', marginTop: '1.75rem', marginBottom: '0.5rem' }}>
              {section.category}
            </h2>
            {section.questions.map((item, qIdx) => {
              const key = `${sIdx}-${qIdx}`;
              return (
                <FaqItem
                  key={key}
                  question={item.q}
                  answer={item.a}
                  isOpen={expanded.has(key)}
                  onToggle={() => toggle(key)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
